<template>
  <section id="work" class="ledger" @keydown.esc="closeAll">
    <div class="container">
      <header class="ledger-head">
        <span ref="ghostEl" class="ghost ledger-ghost" aria-hidden="true">{{
          numeral
        }}</span>

        <!-- The page numbers its beats in one voice: a Fraunces italic
             numeral ahead of the mono kicker, the same pairing the stages
             use. It is section 04 of a real sequence, so the number is
             information; it is aria-hidden only because "zero four" ahead of
             the heading is noise in the ear, not because it is decoration. -->
        <p v-reveal class="eyebrow">
          <span class="eyebrow-n" aria-hidden="true">{{ numeral }}</span>
          <span class="label">{{ $t("ledger.kicker") }}</span>
        </p>

        <h2 v-reveal class="h-section ledger-title" :style="{ '--reveal-i': 1 }">
          {{ $t("ledger.title") }}
        </h2>
        <p v-reveal class="ledger-lede" :style="{ '--reveal-i': 2 }">
          {{ $t("ledger.lede") }}
        </p>
      </header>

      <div
        class="facets"
        role="tablist"
        :aria-label="$t('ledger.facetsLabel')"
      >
        <button
          v-for="(f, i) in facets"
          :key="f.id"
          :ref="(el) => (pillEls[i] = el)"
          class="facet"
          type="button"
          role="tab"
          :aria-selected="f.id === facet ? 'true' : 'false'"
          :tabindex="i === activeIndex ? 0 : -1"
          :class="{ 'is-active': f.id === facet }"
          @click="facet = f.id"
          @keydown="onFacetKey($event, i)"
        >
          {{ $t("ledger.facets." + f.id) }}
          <span class="facet-n">{{ f.count }}</span>
        </button>
      </div>

      <p class="visually-hidden" role="status">
        {{ $t("ledger.showing", { shown, total: rows.length }) }}
      </p>

      <ul class="rows" role="list">
        <template v-for="(p, i) in rows" :key="p.id">
          <LedgerRow
            class="ledger-item ledger-item--row"
            :class="stateClass(p)"
            :project="p"
            :index="i"
            :open="openId === p.id"
            @toggle="toggle(p)"
          />
          <!-- The dossier is a <div>; a <ul> may only contain <li>. It rides
               in a presentational <li> so the list stays valid HTML and the
               shared state class lands on the wrapper rather than on the
               dossier's own root, whose `transition` a parent shorthand
               would otherwise replace and kill the 0fr → 1fr open.
               The wrapper also carries colour channel 3: the 3px rule that
               continues the row's swatch tick down the open dossier. -->
          <li
            class="ledger-item ledger-item--slot"
            :class="stateClass(p)"
            :style="slotVars(p, i)"
            role="presentation"
          >
            <ProjectDossier
              :project="p"
              :open="openId === p.id"
              :id="dossierId(p)"
              @close="closeAll"
            />
          </li>
        </template>
      </ul>
    </div>
  </section>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import LedgerRow from "@/components/project/LedgerRow.vue";
import ProjectDossier from "@/components/project/ProjectDossier.vue";
import { registerLayer, DEPTH } from "@/composables/useDepth";
import { gradientOf } from "@/composables/useProjectAssets";

/* The rail is a fixed set of disciplines; the counts behind it are not.
   A facet with nothing in it is dropped rather than shown as "(0)". */
const FACETS = ["all", "web", "native", "data", "research", "physical"];

/* Must match the .is-out transition duration below (--d-2). */
const OUT_MS = 220;

export default {
  name: "LedgerSection",
  components: { LedgerRow, ProjectDossier },
  props: {
    projects: { type: Array, required: true },
    /* Same API as ProfileSection: the numeral belongs to the page's running
       order rather than to this file. */
    numeral: { type: String, default: "04" },
  },
  setup(props) {
    const facet = ref("all");
    const openId = ref(null);
    const gone = ref(new Set()); // rows whose fade-out has finished
    const ghostEl = ref(null);
    const pillEls = [];
    let stopDepth = null;
    let hideTimer = null;

    const rows = computed(() =>
      props.projects
        .filter((p) => p.active !== false)
        .slice()
        /* An explicit `order` comes first, because Björn ranks the work by
           what he wants seen rather than by date. Anything without one falls
           in behind, newest first. */
        .sort(
          (a, b) =>
            (a.order ?? 999) - (b.order ?? 999) ||
            (b.sortYear || 0) - (a.sortYear || 0)
        )
    );

    const matches = (p, f) => f === "all" || (p.tags || []).includes(f);

    const facets = computed(() =>
      FACETS.map((id) => ({
        id,
        count: rows.value.filter((p) => matches(p, id)).length,
      })).filter((f) => f.count > 0)
    );

    const shown = computed(
      () => rows.value.filter((p) => matches(p, facet.value)).length
    );

    /* Roving tabindex. If the selected facet ever leaves the rail (its count
       drops to 0) the rail must still hold exactly one tab stop, or it falls
       out of the tab order entirely. */
    const activeIndex = computed(() => {
      const i = facets.value.findIndex((f) => f.id === facet.value);
      return i === -1 ? 0 : i;
    });

    /* One class binding carries all four row states, so the row and its
       dossier stay in lockstep without either component knowing about
       filtering or about its siblings. `is-open` is what LedgerRow already
       sets on itself; repeating it here is how the dossier's wrapper learns
       the same thing. */
    const stateClass = (p) => ({
      "is-out": !matches(p, facet.value),
      "is-gone": gone.value.has(p.id),
      "is-dim": openId.value !== null && openId.value !== p.id,
      "is-open": openId.value === p.id,
    });

    /* The dossier's left rule is the project's own gradient, and the wrapper
       shares the row's stagger index so a filter change moves the pair as
       one. Vertical, so it reads as a continuation of the row's tick. */
    const slotVars = (p, i) => ({
      "--p-grad-v": gradientOf(p, 180),
      "--reveal-i": i,
    });

    const dossierId = (p) => `dossier-${p.id}`;
    const toggle = (p) => {
      openId.value = openId.value === p.id ? null : p.id;
    };
    const closeAll = () => {
      openId.value = null;
    };

    watch(facet, (f) => {
      // Filtering only hides. Arriving rows regain layout immediately and
      // fade up in index order; leaving rows keep their layout for the length
      // of their collapse and only then go to display: none. No FLIP, no
      // measurement, no list transition.
      gone.value = new Set(
        [...gone.value].filter((id) => {
          const p = rows.value.find((r) => r.id === id);
          return p && !matches(p, f);
        })
      );

      const open = rows.value.find((r) => r.id === openId.value);
      if (open && !matches(open, f)) openId.value = null;

      clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        gone.value = new Set(
          rows.value.filter((p) => !matches(p, f)).map((p) => p.id)
        );
      }, OUT_MS);
    });

    // Automatic activation: a tablist is expected to move selection with the
    // arrow keys, and only the selected pill is a tab stop.
    function onFacetKey(e, i) {
      const step = { ArrowRight: 1, ArrowLeft: -1 };
      if (!(e.key in step) && e.key !== "Home" && e.key !== "End") return;
      const n = facets.value.length;
      if (!n) return;
      e.preventDefault();
      const j =
        e.key === "Home"
          ? 0
          : e.key === "End"
          ? n - 1
          : (i + step[e.key] + n) % n;
      facet.value = facets.value[j].id;
      if (pillEls[j]) pillEls[j].focus();
    }

    onMounted(() => {
      stopDepth = registerLayer(ghostEl.value, { k: DEPTH.GHOST });
    });

    onBeforeUnmount(() => {
      clearTimeout(hideTimer);
      if (stopDepth) stopDepth();
    });

    return {
      facet,
      facets,
      activeIndex,
      openId,
      rows,
      shown,
      ghostEl,
      pillEls,
      stateClass,
      slotVars,
      dossierId,
      toggle,
      closeAll,
      onFacetKey,
    };
  },
};
</script>

<style scoped>
.ledger {
  /* No bottom padding and no closing rule: the profile section's top
     hairline is this table's last rule. One rule, not two. */
  padding: var(--s-10) 0 0;
  scroll-margin-top: 88px;
}

/* Full container width, so the ghost numeral's `right: 0` lands on the
   container's right edge as it does in every other section. The 62ch measure
   belongs to the copy, not to the positioning context. */
.ledger-head {
  position: relative;
}

/* The only depth layer in the whole section — everything below it is type,
   hairlines and rows, all locked at k = 1.00. */
.ledger-ghost {
  --travel-y: calc(
    (var(--p, 0.5) - 0.5) * (1 - var(--k, 1)) * var(--travel) *
      var(--depth-scale)
  );
  transform: translate3d(var(--mx, 0px), var(--travel-y), 0)
    scale(var(--s, 1));
}

.eyebrow {
  display: flex;
  align-items: baseline;
  gap: 10px;
}

.eyebrow-n {
  /* Named outright, and at 600, for the two reasons MastheadSection already
     documents: --font-display is not defined anywhere in App.vue, so
     var(--font-display, …) is a token that never resolves and the fallback
     does all the work; and index.html requests Fraunces as
     ital,opsz,wght@1,9..144,600 — one italic face at one weight. A declared
     500 has no face to land on, so it either clamps back to 600 or, if
     Fraunces has not arrived, asks Georgia for a medium it does not have and
     gets a synthesised one. */
  font-family: "Fraunces", Georgia, "Times New Roman", serif;
  font-style: italic;
  font-weight: 600;
  font-size: 15px;
  line-height: 1;
  font-optical-sizing: auto;
  font-variant-numeric: lining-nums;
  /* Copper is the page's one chromatic accent, but at 15px it clears AA on
     warm paper only once it is taken a step toward the ink. Same hue, no
     second accent. */
  color: color-mix(in oklab, var(--accent) 82%, var(--ink));
}

.ledger-title {
  margin-top: var(--s-3);
  max-width: 20ch;
}

.ledger-lede {
  margin-top: var(--s-4);
  max-width: 62ch;
  font-size: var(--t-body);
  line-height: 1.62;
  color: var(--text);
  text-wrap: pretty;
}

/* ── Facet rail ─────────────────────────────────────────────── */
.facets {
  display: flex;
  flex-wrap: wrap;
  gap: var(--s-2);
  margin: var(--s-7) 0 var(--s-5);
}

.facet {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 14px;
  border: var(--hair-w) solid var(--hairline);
  border-radius: var(--r-pill);
  font-family: var(--font-measure);
  font-size: 12px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--quiet);
  background-color: transparent;
  transition: color var(--d-1) linear, border-color var(--d-1) linear,
    background-color var(--d-2) var(--ease-inout);
}
.facet:hover {
  border-color: var(--hairline-strong);
  color: var(--ink);
}
.facet.is-active {
  background-color: var(--accent);
  border-color: transparent;
  color: #fff;
}
/* No opacity here. The count is information, not decoration, and --quiet
   faded at 12px would drop under AA. It reads as secondary because it is a
   numeral in a smaller measure, not because it is dimmed. */
.facet-n {
  font-variant-numeric: tabular-nums;
}

/* ── Rows ───────────────────────────────────────────────────── */
.rows {
  list-style: none;
  margin: 0;
  padding: 0;
}

/* One transition for every state this section owns, declared once on the
   shared class. It replaces `.reveal`'s (it is more specific), which is the
   point: a row's first paint and its arrival after a filter change are the
   same motion, so they should not be two different ones. Arrival is
   sequenced by index; leaving, below, is not — exits happen together and
   entrances happen in order, which is what makes the list read as one
   surface rather than seventeen independent ones.

   `--reveal-i` is the row's index, set inline by LedgerRow and, for the
   dossier wrapper, by slotVars(). No JS touches these elements per frame. */
.ledger-item {
  --stagger: calc(var(--reveal-i, 0) * 24ms);
  /* padding-top/bottom and border-top-width travel with the height. They have
     to: box-sizing is border-box, and under border-box a max-height of 0 is
     floored at padding + border rather than absorbing them — measured, an
     otherwise-collapsed row stops at 41px (20 + 20 + 1). Without these three
     the ledger would shrink part-way over 220ms and then snap the last 41px
     per row away in one frame when `is-gone` lands. padding-left stays last
     in the list because it is the only entry the hover rule retimes. */
  transition-property: opacity, transform, max-height, min-height, padding-top,
    padding-bottom, border-top-width, padding-left;
  transition-duration: var(--d-3), var(--d-3), var(--d-3), var(--d-3),
    var(--d-3), var(--d-3), var(--d-3), var(--d-2);
  transition-timing-function: var(--ease-out);
  transition-delay: var(--stagger), var(--stagger), var(--stagger),
    var(--stagger), var(--stagger), var(--stagger), var(--stagger), 0s;
}

/* The rest cap the collapse starts from. It is never reached — the tallest
   row is ~130px — so it changes nothing at rest, and because --ease-out is
   already 60% through its range at t = 0.2 the cap is crossed within ~40ms
   and the reader sees a collapse, not a pause. Height, not a measurement —
   but max-height alone does not get to zero under border-box, so the padding
   and the hairline are animated alongside it (see .ledger-item above). */
.ledger-item--row {
  max-height: 240px;
  /* `clip` keeps the collapse from spilling while still letting a mark's
     drop-shadow past the row edge; `hidden` is the fallback for engines
     without overflow-clip-margin. */
  overflow: hidden;
  overflow: clip;
  overflow-clip-margin: 20px;
}

.ledger-item.is-dim {
  opacity: 0.4;
}

/* Leaving: fade, sink 8px, collapse. 220ms, no stagger, and OUT_MS above is
   the same number — the row goes to display: none only once it is invisible.
   min-height has to travel too: it wins over max-height, so an 88px row
   would otherwise refuse to close. */
.ledger-item.is-out {
  opacity: 0;
  transform: translateY(8px);
  pointer-events: none;
  transition-duration: var(--d-2);
  transition-delay: 0s;
}
.ledger-item--row.is-out {
  max-height: 0;
  min-height: 0;
  padding-top: 0;
  padding-bottom: 0;
  border-top-width: 0;
}

/* `visibility`, not `display`. Both take the row out of the tab order and out
   of the accessibility tree, but a `display: none` element has no
   before-change style, so nothing transitions when it comes back — the
   arrival fade and its 24ms stagger simply never ran and rows popped in at
   full opacity. A `visibility: hidden` element is still laid out (at zero
   height, because is-out has already collapsed it), so the return animates.
   Not in the transition-property list on purpose: visibility flips at the end
   when hiding and at the start when showing, which is exactly right here. */
.ledger-item.is-gone {
  visibility: hidden;
}

/* Hover is CSS state on the row's own root: no listener, no rect read.
   The tick widens and the row's left padding gives by 4px, so the row leans
   toward the reader rather than just tinting. 140ms in, 220ms out — arriving
   faster than it leaves is what gives it weight. */
@media (hover: hover) {
  .ledger-item--row:hover {
    padding-left: 12px;
    transition-duration: var(--d-3), var(--d-3), var(--d-3), var(--d-3),
      var(--d-3), var(--d-3), var(--d-3), var(--d-1);
  }
}

/* The hover rule above carries the same specificity as .is-out but is
   declared later, so a row filtered out from under the pointer would take
   hover's 320ms for the collapse while OUT_MS hides it at 220ms — a visible
   snap. Restating the exit timing after hover keeps the collapse and the JS
   timer on the same number in every state. */
.ledger-item.is-out {
  transition-duration: var(--d-2);
  transition-delay: 0s;
}

/* Colour channel 3 is one continuous 3px rule: it starts as the row's swatch
   tick and runs the full height of the open dossier. The tick is therefore
   laid out at full row height and scaled down at rest, so extending it is a
   composited scaleY rather than an animated height — and the ledger at rest
   still reads as a column of short colour marks. Reaching into LedgerRow is
   deliberate: the rule spans two sibling elements, so neither of them can
   own it alone. */
.ledger-item--row :deep(.tick) {
  top: 0;
  height: 100%;
  margin-top: 0;
  transform: scaleY(0.5);
  transform-origin: 50% 50%;
  transition-property: width, transform;
  transition-duration: var(--d-2), var(--d-4);
  transition-timing-function: var(--ease-out);
}
@media (hover: hover) {
  .ledger-item--row:hover :deep(.tick) {
    transition-duration: var(--d-1), var(--d-4);
  }
}
.ledger-item--row.is-open :deep(.tick) {
  transform: scaleY(1);
  border-radius: 0;
}

.ledger-item--slot {
  position: relative;
  list-style: none;
  display: block;
}
/* The dossier's own 1px left border is replaced, not doubled: channel 3 is
   one rule at one width down the whole open block. */
.ledger-item--slot :deep(.d-inner::before) {
  display: none;
}
.ledger-item--slot::before {
  content: "";
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background-image: var(--p-grad-v);
  opacity: 0;
  /* No height animation: this element's height is the dossier's, so the rule
     lengthens with the 0fr → 1fr open for free. */
  transition: opacity var(--d-2) var(--ease-out);
  pointer-events: none;
}
.ledger-item--slot.is-open::before {
  opacity: 1;
}

.visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

@media (max-width: 720px) {
  .ledger {
    padding-top: var(--s-9);
  }

  /* Bleeds to both edges so the rail reads as a strip, not a clipped row. */
  .facets {
    flex-wrap: nowrap;
    overflow-x: auto;
    scroll-snap-type: x proximity;
    scrollbar-width: none;
    -ms-overflow-style: none;
    margin-inline: calc(var(--edge) * -1);
    padding-inline: var(--edge);
  }
  .facets::-webkit-scrollbar {
    display: none;
  }
  .facet {
    flex: 0 0 auto;
    scroll-snap-align: start;
  }
}

/* Reduced motion: the global clamp in App.vue collapses every duration here,
   which leaves each state as a correct still — filtered rows are simply
   absent, an open dossier still carries its full-height rule. Nothing is
   mid-animation and nothing is blank. */
</style>
